import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import customerActions from '../../Actions/customerActions';
import commonActions from '../../Actions/commonActions';
import helpers from '../../Utils/helpers';
import Card from '../../Components/Card/Card';
import Tabs from '../../Components/Tabs/Tabs';
import InvoicesTable from '../../Components/Customer/Transactions/InvoicesTable';
import TransactionsFilterBar from '../../Components/Customer/Transactions/TransactionsFilterBar';
import TransactionsPagination from '../../Components/Customer/Transactions/TransactionsPagination';
import DetailsDrawer from '../../Components/Customer/Transactions/DetailsDrawer';
import SendInvoiceCopyModal from '../../Components/Customer/Modals/SendInvoiceCopyModal';
import '../../Components/Customer/Transactions/transactions.css';
import useSortingAndFiltering from '../../Hooks/useSortingAndFiltering';
import useUserAccess from '../../Hooks/useUserAccess';
import useModal from '../../Hooks/useModal';
import Modals from '../../Constants/modals';
import { UserRoles } from '../../Constants/userRoles';
import { mergeTransactions } from './mergeTransactions';
import type {
  BalanceSubscription,
  RootState,
  TransactionRow,
  VbInvoice,
} from './CustomerTransactions.types';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyDispatch = any;

const SEARCHABLE_KEYS = ['invoiceNumber'];

const defaultComparer = (a: TransactionRow, b: TransactionRow): number =>
  (b.date ?? '').localeCompare(a.date ?? '');

const isDateInRange = (value: unknown, from: Date | null, to: Date | null): boolean => {
  if (typeof value !== 'string' || value === '') {
    return false;
  }
  const rowYmd = value.slice(0, 10);
  if (from !== null && rowYmd < helpers.formatDate(from)) {
    return false;
  }
  if (to !== null && rowYmd > helpers.formatDate(to)) {
    return false;
  }
  return true;
};

type TabId = 'invoices' | 'usages' | 'deleted';

interface TabConfig {
  id: TabId;
  label: string;
  badge?: ReactNode;
}

const CustomerTransactionsContainer = () => {
  const { t } = useTranslation();
  const dispatch: AnyDispatch = useDispatch();
  const [activeTabId, setActiveTabId] = useState<TabId>('invoices');
  const [searchText, setSearchText] = useState('');
  const [uninvoicedOnly, setUninvoicedOnly] = useState(false);
  const [dateFrom, setDateFrom] = useState<Date | null>(null);
  const [dateTo, setDateTo] = useState<Date | null>(null);
  const [dateResetKey, setDateResetKey] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [sendCopyInvoice, setSendCopyInvoice] = useState<VbInvoice | null>(null);

  const { openModal, isModalOpen } = useModal();
  const { hasAdminAccess } = useUserAccess();
  const isInvoiceAdmin = Boolean(hasAdminAccess(UserRoles.Invoicing));

  const customer = useSelector((state: RootState) => state.CustomerReducer.customer);
  const vbInvoices = useSelector((state: RootState) => state.CustomerReducer.invoices);
  const vossInvoices = useSelector(
    (state: RootState) => state.CustomerReducer.vossCustomerInvoices
  );
  const balance = useSelector((state: RootState) => state.CustomerReducer.vossCustomerBalance);
  const vossInvoiceDetail = useSelector(
    (state: RootState) => state.CustomerReducer.vossCustomerInvoice
  );

  const vbCustomerNumber = customer?.vbCustomer?.customerNumber;
  const vossCustomerId = customer?.vossCustomer?.id;

  useEffect(() => {
    if (vbCustomerNumber !== undefined) {
      dispatch(customerActions.getCustomerInvoices(vbCustomerNumber));
    }
    if (vossCustomerId !== undefined) {
      dispatch(customerActions.getVossCustomerInvoices(vossCustomerId));
      dispatch(customerActions.getVossCustomerBalance(vossCustomerId));
    }
  }, [dispatch, vbCustomerNumber, vossCustomerId]);

  const isLoading =
    (vbCustomerNumber !== undefined && vbInvoices === null) ||
    (vossCustomerId !== undefined && (vossInvoices === null || balance === null));

  const rows = useMemo(
    () => mergeTransactions(vbInvoices ?? [], vossInvoices ?? [], balance?.subscriptions ?? []),
    [vbInvoices, vossInvoices, balance]
  );

  const selectedRow = useMemo(
    () => (selectedRowId === null ? null : (rows.find((row) => row.id === selectedRowId) ?? null)),
    [rows, selectedRowId]
  );

  const uninvoicedCount = useMemo(() => rows.filter((row) => row.isUninvoiced).length, [rows]);

  const customFilterKeys = useMemo(
    () => [
      ...(uninvoicedOnly
        ? [{ key: 'isUninvoiced', condition: (value: unknown) => value === true }]
        : []),
      ...(dateFrom !== null || dateTo !== null
        ? [{ key: 'date', condition: (value: unknown) => isDateInRange(value, dateFrom, dateTo) }]
        : []),
    ],
    [uninvoicedOnly, dateFrom, dateTo]
  );

  const { filteredAndSortedList, sortKey, sortOrder, handleSortChange } =
    useSortingAndFiltering<TransactionRow>({
      list: rows,
      searchText,
      searchableKeys: SEARCHABLE_KEYS,
      customFilterKeys,
      defaultComparer,
    });

  useEffect(() => {
    setPage(1);
  }, [searchText, uninvoicedOnly, dateFrom, dateTo, sortKey, sortOrder, pageSize]);

  const totalItems = filteredAndSortedList.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filteredAndSortedList.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const bumpDateReset = () => setDateResetKey((key) => key + 1);
  const toggleUninvoicedOnly = () => setUninvoicedOnly((prev) => !prev);

  const clearDateFrom = () => {
    setDateFrom(null);
    bumpDateReset();
  };

  const clearDateTo = () => {
    setDateTo(null);
    bumpDateReset();
  };

  const clearAllFilters = () => {
    setSearchText('');
    setDateFrom(null);
    setDateTo(null);
    setUninvoicedOnly(false);
    bumpDateReset();
  };

  const openDetails = (row: TransactionRow) => {
    setSelectedRowId(row.id);
    if (!row.isUninvoiced && row.vossId !== undefined && vossCustomerId !== undefined) {
      dispatch(customerActions.getVossCustomerInvoice(vossCustomerId, row.vossId));
    }
  };

  const closeDetails = () => {
    setSelectedRowId(null);
    dispatch(customerActions.storeVossCustomerInvoice(null));
  };

  const openSendCopy = (row: TransactionRow) => {
    const vbInvoice =
      vbInvoices?.find(
        (invoice) => invoice.id === row.vbId && invoice.invoiceNumber === row.invoiceNumber
      ) ?? null;
    if (vbInvoice === null || vbCustomerNumber === undefined) {
      dispatch(commonActions.showError(t('customertransactions.sendCopyUnavailable')));
      return;
    }
    setSendCopyInvoice(vbInvoice);
    openModal(Modals.SendInvoiceCopy);
  };

  const showPackages =
    selectedRow !== null && (selectedRow.isUninvoiced || selectedRow.vossId !== undefined);

  const drawerPackages = useMemo<BalanceSubscription[]>(() => {
    if (selectedRow === null) {
      return [];
    }
    if (selectedRow.isUninvoiced) {
      const sub = balance?.subscriptions?.find((s) => s.id === selectedRow.vossId);
      return sub !== undefined ? [sub] : [];
    }
    return vossInvoiceDetail?.invoice?.subscriptions ?? [];
  }, [selectedRow, balance, vossInvoiceDetail]);

  const isLoadingPackages =
    selectedRow !== null &&
    !selectedRow.isUninvoiced &&
    selectedRow.vossId !== undefined &&
    vossInvoiceDetail?.loading === true;

  const selectedVbInvoice = useMemo(() => {
    if (selectedRow?.vbId === undefined) {
      return null;
    }
    return vbInvoices?.find((invoice) => invoice.id === selectedRow.vbId) ?? null;
  }, [selectedRow, vbInvoices]);

  const counts: Record<TabId, number | undefined> = {
    invoices: isLoading ? undefined : rows.length,
    usages: undefined,
    deleted: undefined,
  };

  const renderCount = (count: number | undefined, isActive: boolean): ReactNode =>
    count !== undefined && count > 0 ? (
      <span
        className={`transactions-tab-count${isActive ? ' transactions-tab-count--active' : ''}`}
      >
        {count}
      </span>
    ) : undefined;

  const tabs: TabConfig[] = [
    {
      id: 'invoices',
      label: t('customertransactions.invoices'),
      badge: renderCount(counts.invoices, activeTabId === 'invoices'),
    },
    {
      id: 'usages',
      label: t('customertransactions.usages'),
      badge: renderCount(counts.usages, activeTabId === 'usages'),
    },
    {
      id: 'deleted',
      label: t('customertransactions.deleted'),
      badge: renderCount(counts.deleted, activeTabId === 'deleted'),
    },
  ];

  const activeTab = tabs.find((tab) => tab.id === activeTabId);

  const renderActiveTabContent = (): ReactNode => {
    switch (activeTabId) {
      case 'invoices':
        return (
          <>
            <TransactionsFilterBar
              searchText={searchText}
              onSearchChange={setSearchText}
              dateFrom={dateFrom}
              dateTo={dateTo}
              onDateFromChange={setDateFrom}
              onDateToChange={setDateTo}
              onClearDateFrom={clearDateFrom}
              onClearDateTo={clearDateTo}
              dateResetKey={dateResetKey}
              uninvoicedOnly={uninvoicedOnly}
              onToggleUninvoicedOnly={toggleUninvoicedOnly}
              uninvoicedCount={uninvoicedCount}
              onClearAll={clearAllFilters}
              shownCount={filteredAndSortedList.length}
              totalCount={rows.length}
            />
            <InvoicesTable
              rows={pageRows}
              sortKey={sortKey}
              sortOrder={sortOrder}
              onSort={handleSortChange}
              isInvoiceAdmin={isInvoiceAdmin}
              onOpenDetails={openDetails}
              onSendCopy={openSendCopy}
              selectedRow={selectedRow}
            />
            {totalItems > 0 && (
              <TransactionsPagination
                page={currentPage}
                pageSize={pageSize}
                totalItems={totalItems}
                onPageChange={setPage}
                onPageSizeChange={setPageSize}
              />
            )}
            {selectedRow !== null && (
              <DetailsDrawer
                row={selectedRow}
                onClose={closeDetails}
                showPackages={showPackages}
                packages={drawerPackages}
                isLoadingPackages={isLoadingPackages}
                vbInvoice={selectedVbInvoice}
                isInvoiceAdmin={isInvoiceAdmin}
                onSendCopy={openSendCopy}
              />
            )}
            {isModalOpen(Modals.SendInvoiceCopy) &&
              sendCopyInvoice !== null &&
              vbCustomerNumber !== undefined && (
                <SendInvoiceCopyModal customerNumber={vbCustomerNumber} invoice={sendCopyInvoice} />
              )}
          </>
        );
      case 'usages':
        return <p className="mb-0">{t('customertransactions.usagesPlaceholder')}</p>;
      case 'deleted':
        return <p className="mb-0">{t('customertransactions.deletedPlaceholder')}</p>;
      default:
        return null;
    }
  };

  return (
    <Card>
      <Card.Header>
        <h3 className="card-title">{t('customertransactions.header')}</h3>
      </Card.Header>
      <Tabs
        tabs={tabs}
        activeTab={activeTab}
        setActiveTab={(tab: TabConfig) => setActiveTabId(tab.id)}
      />
      <Card.Content isLoading={activeTabId === 'invoices' && isLoading} hasNoData={false}>
        {renderActiveTabContent()}
      </Card.Content>
    </Card>
  );
};

export default CustomerTransactionsContainer;
